import React from 'react';
import classNames from 'classnames';
import { IPurchaseOrdersProps } from './PurchaseOrders.properties';
import PurchaseOrderSearchLookUpDrawer from '../common/SearchLookUpDrawer/Containers/PurchaseOrders';
import { ILocationState, useModule } from 'utils/hooks';
import { DisabledChangeStatus, DisabledSearchLookUpDrawerTabs, getTabs } from './PurchaseOrders.constants';
import modules from './PurchaseOrders.modules';
import { ActionBar, BreadcrumbBar, NavigationTabs } from '@markinson/uicomponents-v2';
import { ActionBarContext } from 'utils/ActionBarContextProvider';
import OptionsMenu from 'components/common/OptionsMenu';
import { Route, withRouter } from 'react-router';
import Lines from './Lines';
import { withStyles } from '@material-ui/core';
import styles from './PurchaseOrders.styles';
import { useTabLocations } from './PurchaseOrders.hooks';
import { ILinesHandle } from './Lines/Lines.properties';
import LoadPurchaseOrderDialog from './LoadPurchaseOrderDialog';
import OrderDetails from './OrderDetails';
import Delivery from './Delivery';
import { IDeliveryDetailsHandle } from './Delivery/Delivery.properties';
import { PurchaseOrdersActionTypes } from './PurchaseOrders.actions';
import NewPurchaseOrderDialog from './NewPurchaseOrderDialog';
import { usePOContextSelector, usePODispatch } from './PurchaseOrders.context';
import SummaryPanel from './SummaryPanel';
import LineDetails from './LineDetails';
import { IOrderDetailsHandle } from './OrderDetails/OrderDetails.properties';
import { ILineDetailsHandle } from './LineDetails/LineDetails.properties';
import ProcessPurchaseOrdersDialogs from './ProcessPurchaseOrdersDialogs';
import { IProcessPurchaseOrdersDialogsHandle } from './ProcessPurchaseOrdersDialogs/ProcessPurchaseOrdersDialogs.properties';
import Notepad from 'components/common/NotepadV2';
import { INotepadHandle } from 'components/common/NotepadV2/Notepad.properties';
import Diary from 'components/common/DiaryV2/Diary';
import DiaryDetails from 'components/common/DiaryV2/DiaryDetails';
import { IDiaryDetailsHandle } from 'components/common/DiaryV2/DiaryDetails/DiaryDetails.properties';
import { Operation } from 'utils/operations';
import Commit from './Commit';
import LineCommitments from './LineCommitments';
import { useRetrievePurchaseOrdersSummary } from 'api/purchaseOrders/purchaseOrders';
import getOptions from './PurchaseOrders.options';
import filterOptions from './PurchaseOrder.filterRow';
import PurchaseOrderLookup from 'components/common/PurchaseOrderLookup';
import PrintDialog from 'components/common/PrintDialog';

const PurchaseOrders = (props: IPurchaseOrdersProps) => {
    const { classes, match, history, location, isMenuOptionOpen, isPurchaseOrdersLookUpOpen, selectedPurchaseOrder, toggleMenuOption, changeOperationMode, setLookupData, staff, resetSearchLookupDrawer } = props;
    const [loadPurchaseOrderDialogOpen, setLoadPurchaseOrderDialogOpen] = React.useState(false);
    const [newPurchaseOrderDialogOpen, setNewPurchaseOrderDialogOpen] = React.useState(false);
    const [printPODialogOpen, setPrintPODialogOpen] = React.useState(false);
    const reloadPurchaseOrder = usePOContextSelector<'ReloadPurchaseOrder'>((state) => state.ReloadPurchaseOrder);
    const POSummary = usePOContextSelector<'SummaryPanelData'>((state) => state.SummaryPanelData);
    const tabsData = React.useMemo(() => getTabs(POSummary?.PurchaseOrderStatus, selectedPurchaseOrder), [POSummary, selectedPurchaseOrder]);
    const optionsMenuItems = React.useMemo(() => getOptions(POSummary?.PurchaseOrderStatus), [POSummary]);
    const contextDispatch = usePODispatch();
    const { actionBarProps } = React.useContext(ActionBarContext);
    const url = match ? match.url : '';
    const path = match ? match.path : '';
    const linesRef = React.useRef<ILinesHandle>();
    const lineDetailsPageRef = React.useRef<ILineDetailsHandle>();
    const detailsRef = React.useRef<IOrderDetailsHandle>();
    const deliveryRef = React.useRef<IDeliveryDetailsHandle>();
    const notepadPageRef = React.useRef<INotepadHandle>();
    const diaryDetailsPageRef = React.useRef<IDiaryDetailsHandle>();
    const processPurchaseOrdersRef = React.useRef<IProcessPurchaseOrdersDialogsHandle>();
    const { tabLocations } = useTabLocations(url, location, selectedPurchaseOrder);

    const fetchSummary = useRetrievePurchaseOrdersSummary(usePOContextSelector<'PurchaseOrder'>((state) => state.PurchaseOrder));

    const { selectedTab, handleModuleChange } = useModule(
        history,
        location,
        'Lines',
        tabLocations,
        modules,
    );
    const [purchaseOrderLookupValue, setPurchaseOrderLookupValue] = React.useState<any>();

    React.useEffect(
        () => {
            contextDispatch({
                [PurchaseOrdersActionTypes.setPurchaseOrder]: selectedPurchaseOrder,
            });
        },
        [selectedPurchaseOrder]
    );

    React.useEffect(
        () => {
            if (reloadPurchaseOrder) {
                ReloadPuchaseOrder();
            }
        },
        [reloadPurchaseOrder]
    );

    React.useEffect(
        () => {
            if (!selectedPurchaseOrder) {
                handleModuleChange('Lines');
            }
        },
        [selectedPurchaseOrder]
    );

    React.useEffect(
        () => {
            changeOperationMode(Operation.BROWSE);

            return () => {
                props.resetSearchLookupDrawer();
            };
        },
        []
    );

    React.useEffect(
        () => {
            const handleKeyDown = (event: KeyboardEvent) => {
                if (event.ctrlKey && event.key.toLowerCase() === 'enter' && selectedTab === 'Lines' && linesRef?.current?.getLinesCount()) {
                    event.preventDefault();
                    event.stopPropagation();
                    processPurchaseOrdersRef.current?.process();
                }
            };

            window.addEventListener('keydown', handleKeyDown, true);

            return () => {
                window.removeEventListener('keydown', handleKeyDown, true);
            };
        },
        [selectedTab]
    );

    const ReloadPuchaseOrder = React.useCallback(
        () => {
            linesRef?.current?.refreshDataGrid();
            fetchSummary?.refetch();
            _handleModuleChange('Lines');
            contextDispatch({
                [PurchaseOrdersActionTypes.setReloadPurchaseOrder]: false,
            });
        },
        [linesRef]
    );

    const handleModuleChangeExtended = React.useCallback(
        (tabToNavigate, inlineObject?: ILocationState) => {
            if (DisabledChangeStatus.includes(tabToNavigate)) {
                contextDispatch({
                    [PurchaseOrdersActionTypes.setIsChangeStatusDisabled]: true,
                });
            } else {
                contextDispatch({
                    [PurchaseOrdersActionTypes.setIsChangeStatusDisabled]: false,
                });
            }

            handleModuleChange(tabToNavigate, inlineObject ?? { search: null });
        },
        [selectedTab, selectedPurchaseOrder, handleModuleChange]
    );

    const _handleModuleChange = React.useCallback(
        (tabToNavigate, inlineObject?: ILocationState) => {
            if (`${selectedPurchaseOrder}` === '') return;

            if (selectedTab === 'LineDetails') {
                if (lineDetailsPageRef.current?.isChangesSavedOrDiscarded()) {
                    lineDetailsPageRef.current?.onCancel();
                }

                return;
            }

            if (selectedTab !== tabToNavigate && selectedTab === 'Delivery') {
                if (deliveryRef.current?.isChangesSavedOrDiscarded()) {
                    deliveryRef.current?.onCancel();
                } else {
                    return;
                }
            }

            if (selectedTab !== tabToNavigate && selectedTab === 'PurchaseOrderDetails') {
                if (detailsRef.current?.isChangesSavedOrDiscarded()) {
                    detailsRef.current?.OrderDetailsCancel(() => { handleModuleChangeExtended(tabToNavigate); });
                }

                return;
            }

            if (selectedTab === 'PurchaseOrdersDiaryDetails' && inlineObject === undefined && diaryDetailsPageRef.current?.isChangesSavedOrDiscarded()) {

                return;
            } else {
                diaryDetailsPageRef.current?.DiaryDetailsOnCancel(() => handleModuleChangeExtended(tabToNavigate, searchParams()));
            }

            if (selectedTab === 'PurchaseOrdersNotepad' && inlineObject === undefined && notepadPageRef.current?.isChangesSavedOrDiscarded()) {

                return;
            }

            handleModuleChangeExtended(tabToNavigate, inlineObject ?? { search: null });
        },
        [selectedTab, selectedPurchaseOrder, handleModuleChangeExtended]
    );

    const handleToggleMenuOptionOpen = React.useCallback(
        () => {
            toggleMenuOption(!isMenuOptionOpen);
        },
        [isMenuOptionOpen, toggleMenuOption]
    );

    const searchParams = React.useCallback(
        () => ({
            search: {
                PurchaseOrder: selectedPurchaseOrder
            }
        }),
        [selectedPurchaseOrder]
    );

    const handleActionBarButtonClick = React.useCallback(
        (action: string) => {
            switch (action) {
                case 'Back':
                    switch (selectedTab) {
                        case 'PurchaseOrderLineCommitments':
                            _handleModuleChange('Commit');
                            break;
                        case 'Details':
                        case 'Commit':
                            _handleModuleChange('Lines');
                            break;
                        default:
                    }
                    break;
                case 'Load':
                    setLoadPurchaseOrderDialogOpen(true);
                    break;
                case 'NewOrder':
                    setNewPurchaseOrderDialogOpen(true);
                    break;
                case 'Process':
                    processPurchaseOrdersRef.current?.process();
                    break;

                case 'Print':
                    setPrintPODialogOpen(true);
                    break;

                case 'OrderDetailsOk':
                case 'OrderDetailsCancel':
                    detailsRef.current?.[action](() => { handleModuleChangeExtended('Lines'); });
                    break;
                case 'NotepadOk':
                case 'NotepadCancel':
                case 'NotepadEdit':
                    notepadPageRef.current?.[action]();
                    break;
                case 'AddDiary':
                case 'DiaryDetailsNew':
                    changeOperationMode(Operation.NEW);
                    _handleModuleChange('PurchaseOrdersDiaryDetails', searchParams());
                    break;
                case 'DiaryDetailsOk':
                    diaryDetailsPageRef.current?.[action]((SearchObj) => handleModuleChangeExtended('PurchaseOrdersDiaryDetails', SearchObj));
                    break;
                case 'DiaryDetailsCancel':
                case 'DiaryDetailsDelete':
                case 'DiaryDetailsEdit':
                    diaryDetailsPageRef.current?.[action](() => _handleModuleChange('PurchaseOrdersDiary', searchParams()));
                    break;
                default:
            }
        },
        [selectedTab, selectedPurchaseOrder, detailsRef, _handleModuleChange, handleModuleChangeExtended]
    );

    return (
        <div className={classes.mainDiv}>
            <div className={classes.innerContainer}>
                <PurchaseOrderSearchLookUpDrawer
                    enableToggle
                    disabled={DisabledSearchLookUpDrawerTabs.includes(selectedTab)}
                    customSearch={
                        <PurchaseOrderLookup
                            size={'medium'}
                            placeholder={'Search for purchase orders...'}
                            LookupLabel=''
                            suppressDescription={true}
                            value=''
                            display={purchaseOrderLookupValue?.Display || ''}
                            onSelectedItemChange={(item: any) => {
                                setPurchaseOrderLookupValue(item);
                                if (item) {
                                    const listItem = {
                                        Entity: item?.Details.Entity,
                                        PurchaseOrder: item?.Code,
                                        Warehouse: item?.Details.Warehouse,
                                        SupplierDisplay: item?.Details.SupplierDisplay,
                                        SupplierLabel: item?.Details.SupplierLabel,
                                        Status: item?.Details.StatusLabel,
                                        Created: item?.Details.Created,
                                    };

                                    resetSearchLookupDrawer();
                                    setLookupData(listItem);
                                    setPurchaseOrderLookupValue({});
                                }
                            }
                            }
                        />
                    }
                    initialValues={{
                        Finalised: false,
                        View: staff?.defaultView || '',
                    }}
                    customFilterRow={
                        { ...filterOptions, parameters: filterOptions?.parameters as any }
                    }
                />
                <div
                    className={classNames(classes.PSSection, {
                        [classes.PSSectionMarginOpen]: isPurchaseOrdersLookUpOpen,
                        [classes.PSSectionMarginClose]: !isPurchaseOrdersLookUpOpen
                    })}
                >
                    <BreadcrumbBar
                        mainModule={'Purchase Orders'}
                        activeModuleId={selectedTab}
                        moduleTree={modules}
                        onBreadcrumbClick={_handleModuleChange}
                        onMainModuleClick={() => _handleModuleChange('Lines')}
                        onOptionsMenuClick={handleToggleMenuOptionOpen}
                    />
                    <SummaryPanel />
                    {tabsData.some(({ id }) => id === selectedTab) && <NavigationTabs
                        lookupOpen={isPurchaseOrdersLookUpOpen}
                        tabsData={tabsData}
                        activeTabId={selectedTab}
                        onTabChange={_handleModuleChange}
                    />}
                    <Route
                        key={'Lines'}
                        path={path}
                        exact
                        render={() => <Lines forwardRef={linesRef} changeSelectedTab={handleModuleChangeExtended} />}
                    />
                    <Route
                        key={'PurchaseOrderDetails'}
                        path={`${path.endsWith('/') ? path : `${path}/`}order-details`}
                        exact
                        render={() => <OrderDetails innerRef={detailsRef} />}
                    />
                    <Route
                        key={'Delivery'}
                        path={`${path.endsWith('/') ? path : `${path}/`}delivery`}
                        exact
                        render={() => <Delivery innerRef={deliveryRef} changeSelectedTab={handleModuleChangeExtended} />}
                    />
                    <Route
                        key={'LineDetails'}
                        path={`${path.endsWith('/') ? path : `${path}/`}line-details`}
                        exact
                        render={(routeProps) => <LineDetails {...routeProps} innerRef={lineDetailsPageRef} changeSelectedTab={handleModuleChangeExtended} />}
                    />
                    <Route
                        key={'PurchaseOrdersNotepad'}
                        path={`${path.endsWith('/') ? path : `${path}/`}notepad`}
                        exact
                        render={() => (
                            <Notepad
                                innerRef={notepadPageRef}
                                module={{
                                    name: 'PurchaseOrders',
                                    selectedItem: selectedPurchaseOrder,
                                    tableId: 'porder',
                                    entity: 'PurchaseOrder'
                                }}
                            />
                        )}
                    />
                    <Route
                        key={'PurchaseOrdersDiary'}
                        path={`${path.endsWith('/') ? path : `${path}/`}purchase-orders-diary`}
                        exact
                        render={() => (
                            <Diary
                                module={{
                                    name: 'PurchaseOrders',
                                    selectedItem: selectedPurchaseOrder,
                                    summaryDetails: POSummary,
                                    tableId: 'porder',
                                    entity: 'PurchaseOrder'
                                }}
                                gridProps={{
                                    showDetailOnClick: (options) => {
                                        const locationObj = {
                                            search: {
                                                DiaryId: options?.DiaryId,
                                                PurchaseOrder: selectedPurchaseOrder,
                                            },
                                        };
                                        handleModuleChangeExtended('PurchaseOrdersDiaryDetails', locationObj);
                                    },
                                }}
                            />
                        )}
                    />
                    <Route
                        key={'PurchaseOrdersDiaryDetails'}
                        path={`${path.endsWith('/') ? path : `${path}/`}purchase-orders-diary-details`}
                        exact
                        render={(routeProps) => (
                            <DiaryDetails
                                {...routeProps}
                                module={{
                                    name: 'PurchaseOrders',
                                    selectedItem: selectedPurchaseOrder,
                                    summaryDetails: POSummary,
                                    tableId: 'porder',
                                    entity: 'PurchaseOrder'
                                }}
                                innerRef={diaryDetailsPageRef}
                            />
                        )}
                    />
                    <Route
                        key={'Commit'}
                        path={`${path.endsWith('/') ? path : `${path}/`}commit`}
                        exact
                        render={() => <Commit changeSelectedTab={handleModuleChangeExtended} />}
                    />
                    <Route
                        key={'PurchaseOrderLineCommitments'}
                        path={`${path.endsWith('/') ? path : `${path}/`}line-commitments`}
                        exact
                        render={() => <LineCommitments />}
                    />
                </div>
            </div>
            <OptionsMenu
                open={isMenuOptionOpen}
                options={optionsMenuItems?.PO}
                onOptionClick={_handleModuleChange}
                defaultValue={selectedTab}
            />
            <ActionBar {...actionBarProps} onActionClick={handleActionBarButtonClick} />
            {
                loadPurchaseOrderDialogOpen ? (
                    <LoadPurchaseOrderDialog
                        open={loadPurchaseOrderDialogOpen}
                        initialValues={{}}
                        valuesSchema={{}}
                        onClose={() => setLoadPurchaseOrderDialogOpen(false)}
                        linesRef={linesRef}
                    />
                ) : undefined
            }
            {
                newPurchaseOrderDialogOpen ? (
                    <NewPurchaseOrderDialog
                        open={newPurchaseOrderDialogOpen}
                        initialValues={{}}
                        valuesSchema={{}}
                        onClose={() => setNewPurchaseOrderDialogOpen(false)}
                        resetFastEntry={() => { linesRef.current.fastEntryResetToDefault(); }}
                        linesRef={linesRef}
                    />
                ) : undefined
            }
            {
                printPODialogOpen ? (
                    <PrintDialog
                        open={printPODialogOpen}
                        onCancel={() => setPrintPODialogOpen(false)}
                        programId={selectedPurchaseOrder}
                        entityCode={POSummary?.Entity}
                        supplierId={POSummary?.Supplier}
                        documentType={'PO'}
                        fieldId={'PurchaseOrder'}
                    />
                ) : undefined
            }
            <ProcessPurchaseOrdersDialogs
                ref={processPurchaseOrdersRef}
                linesRef={linesRef}
            />
        </div >
    );
};

export default withRouter(withStyles(styles, { index: 1 })(PurchaseOrders));
